import { Box, Typography, Link } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Helmet } from 'react-helmet';
import Fonts from './Fonts';
import { themeColors } from '../styles/theme';
import TopNav from './TopNav';
import AdSense from './AdSense';
import ScrollIndicator from './ScrollIndicator';
import { Footer } from './Footer';

interface TermsOfUseProps {
    setTitle: Dispatch<SetStateAction<string>>;
    setShowBurgerMenuIcon: Dispatch<SetStateAction<boolean>>;
}

const TermsOfUse = ({
   setTitle,
   setShowBurgerMenuIcon
}: TermsOfUseProps) => {
    const [localTitle, setLocalTitle] = useState<string>("Terms of Use");

    useEffect(() => {
        setTitle("Terms of Use");
        setLocalTitle("Terms of Use");
        setShowBurgerMenuIcon(true);
        
        // Update document title for SEO
        document.title = "Terms of Use | Suntap";
        
        // Scroll to the top of the page
        window.scrollTo(0, 0);
    }, // eslint-disable-next-line
        []);

    return (
        <>
        <Helmet>
            <title>Terms of Use | Suntap</title>
            <meta name="description" content="Read the Terms of Use for Suntap, the UK's premier sun tracking app for pub gardens. Learn about our service terms, user responsibilities, and usage guidelines." />
            <link rel="canonical" href="https://sun-tap.com/terms-of-use" />
            <meta name="keywords" content="terms of use, suntap terms, service agreement, user agreement, legal terms, pub garden app terms" />
            <meta property="og:title" content="Terms of Use | Suntap" />
            <meta property="og:description" content="Read the Terms of Use for Suntap, the UK's premier sun tracking app for pub gardens." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://sun-tap.com/terms-of-use" />
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:title" content="Terms of Use | Suntap" />
            <meta name="twitter:description" content="Read the Terms of Use for Suntap, the UK's premier sun tracking app for pub gardens." />
        </Helmet>
        <Fonts />
        <TopNav title={localTitle} />

        <Box 
            sx={{
                width: '100%',
                maxWidth: '900px',
                margin: '0 auto',
                padding: { xs: '0px 16px 16px', sm: '0px 24px 20px' },
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mt: 1,
            }}
        >
            {/* Text Content Box */}
            <Typography
                variant="h1"
                sx={{
                    fontSize: '1.75rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Terms of Use
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                These Terms were last updated on April 15, 2025.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Introduction
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                Welcome to Suntap. These Terms of Use govern your use of our web application and services (collectively referred to as the "Service"). By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access the Service.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Definitions
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                <ul style={{ paddingLeft: '20px'}}>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>"Service"</strong> refers to the Suntap web application and website.
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>"User"</strong> refers to any individual accessing or using the Service.
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>"User Content"</strong> refers to any information, data, or content that users submit to the Service, including but not limited to pub garden drawings.
                    </li>
                </ul>
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Use of the Service
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Acceptable Use
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}>Use the Service in any way that violates any applicable law or regulation</li>
                    <li style={{ marginBottom: '4px' }}>Use the Service to send, knowingly receive, upload, or download any material that is defamatory, offensive, or otherwise objectionable</li>
                    <li style={{ marginBottom: '4px' }}>Attempt to gain unauthorized access to any part of the Service</li>
                    <li style={{ marginBottom: '4px' }}>Use the Service to transmit any material that contains viruses, Trojan horses, or other harmful code</li>
                    <li style={{ marginBottom: '4px' }}>Interfere with or disrupt the integrity or performance of the Service</li>
                    <li style={{ marginBottom: '4px' }}>Harvest or collect information about other users without their consent</li>
                    <li style={{ marginBottom: '4px' }}>Impersonate another person or entity</li>
                </ul>
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Service Availability
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                We aim to ensure that the Service is available consistently. However, we do not guarantee that the Service will be available without interruption or error-free. We reserve the right to modify, suspend, or discontinue the Service at any time without notice.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                User Content
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Garden Submissions
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                When you submit garden drawings or other content to the Service:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}>You retain ownership of your User Content</li>
                    <li style={{ marginBottom: '4px' }}>You grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, and distribute your User Content for the purpose of providing and improving the Service</li>
                    <li style={{ marginBottom: '4px' }}>You represent and warrant that you own or have the necessary rights to the User Content you submit</li>
                    <li style={{ marginBottom: '4px' }}>You are responsible for the accuracy and quality of the User Content you submit</li>
                </ul>
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Moderation
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                We reserve the right, but not the obligation, to:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}>Monitor and review User Content</li>
                    <li style={{ marginBottom: '4px' }}>Edit, remove, or refuse to post any User Content</li>
                    <li style={{ marginBottom: '4px' }}>Take any action with respect to User Content that we deem necessary or appropriate</li>
                </ul>
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                No Obligation to Store
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                We are not obligated to store or backup any User Content. You are responsible for maintaining your own backup of any important information.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Intellectual Property
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Our Intellectual Property
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                }}
            >
                The Service and its original content, features, and functionality are owned by Suntap and are protected by copyright and other intellectual property laws.
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Third-Party Intellectual Property
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                The Service may contain content from third parties, including Google Maps. This content is the property of its respective owners and is protected by copyright and other intellectual property laws.
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Google Maps
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                This application includes Google Maps features and content. Use of Google Maps features and content is subject to the <a href="https://maps.google.com/help/terms_maps/" target="_blank" rel="noopener noreferrer" style={{ color: themeColors.blue }}>Google Maps/Google Earth Additional Terms of Service</a> and the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: themeColors.blue }}>Google Privacy Policy</a>.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Disclaimers
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Accuracy of Information
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                The Service provides sun exposure predictions based on 3D geometry and calculations. While we strive for accuracy, we do not guarantee:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}>The absolute accuracy of sun exposure predictions</li>
                    <li style={{ marginBottom: '4px' }}>The current condition of pub gardens shown in the Service</li>
                    <li style={{ marginBottom: '4px' }}>That any particular pub garden will be sunny at the predicted times</li>
                    <li style={{ marginBottom: '4px' }}>Weather conditions, which are not factored into our calculations</li>
                </ul>
                Our predictions show where sun would hit in clear conditions and do not account for weather, temporary structures, or recent changes to buildings or landscapes.
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Availability of Pubs
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                We do not guarantee that pubs shown in the Service:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}>Are currently open for business</li>
                    <li style={{ marginBottom: '4px' }}>Have accurate operating hours</li>
                    <li style={{ marginBottom: '4px' }}>Have not changed their garden layout since data was collected</li>
                    <li style={{ marginBottom: '4px' }}>Will have the exact facilities shown or described</li>
                </ul>
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Sunshine and Sensibility
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                Finding the perfect sunny spot is wonderful, but please remember:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}>We're not responsible for sunburns, hangovers, or that friend who always wants "just one more round"</li>
                    <li style={{ marginBottom: '4px' }}>Apply sunscreen when enjoying those sunny beer gardens</li>
                    <li style={{ marginBottom: '4px' }}>Stay hydrated between pints</li>
                    <li style={{ marginBottom: '4px' }}>Drink responsibly and know your limits</li>
                    <li style={{ marginBottom: '4px' }}>Arrange safe transportation home</li>
                </ul>
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Limitation of Liability
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                To the maximum extent permitted by law, in no event shall Suntap, its directors, employees, partners, agents, suppliers, or affiliates be liable for:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}>Any indirect, incidental, special, consequential, or punitive damages</li>
                    <li style={{ marginBottom: '4px' }}>Any loss of profits, data, use, or goodwill</li>
                    <li style={{ marginBottom: '4px' }}>Any damages for personal or bodily injury</li>
                    <li style={{ marginBottom: '4px' }}>Any other damages of any kind arising from or in any way connected with:
                        <ul style={{ paddingLeft: '20px', marginTop: '4px' }}>
                            <li>The use or inability to use the Service</li>
                            <li>The content and materials on the Service</li>
                            <li>Unauthorized access to or use of our servers or data</li>
                            <li>Any bugs, viruses, or other harmful code that may be transmitted through the Service</li>
                        </ul>
                    </li>
                </ul>
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Indemnification
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                You agree to defend, indemnify, and hold harmless Suntap and its licensees, licensors, employees, contractors, agents, officers, and directors from and against any claims, damages, obligations, losses, liabilities, costs, and expenses arising from your use of the Service.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Termination
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms.
                <br /><br />
                Upon termination, your right to use the Service will immediately cease. All provisions of these Terms which by their nature should survive termination shall survive termination.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Governing Law
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                These Terms shall be governed by and construed in accordance with the laws of the United Kingdom, without regard to its conflict of law provisions.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Changes to These Terms
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                We reserve the right to modify or replace these Terms at any time. We will provide notice of any significant changes by posting the new Terms on this page and updating the effective date.
                <br /><br />
                By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised Terms. If you do not agree to the new Terms, you may not use the Service.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Contact Us
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                If you have any questions about these Terms, please contact us at:
                <br />
                Email: info@sun-tap.com
            </Typography>

            {/* Advertisement Section - Not in a Paper 
            <Box sx={{ width: '100%', my: 1, borderRadius: '40px' }}>
                <AdSense
                    client="ca-pub-9983613093656329"
                    slot="2973327869"
                    format="auto"
                    responsive="true"
                    width="100%"
                    height="250px"
                />
            </Box>*/}

            <Footer />
        </Box>

        <ScrollIndicator
            forceVisible={true}
            iconColor={themeColors.orange}
            backgroundColor="rgba(255, 255, 255, 0.8)"
        />
        </>
    );
};

export default TermsOfUse;